import { resolveCarTotalFromBreakdown } from './auto-premium-breakdown';
import type { PremiumBreakdownMap } from './auto-premium-breakdown';

/**
 * Calculates the premium amount attributable to a specific vehicle on an auto policy.
 *
 * Each car's base premium is resolved by `resolveCarTotalFromBreakdown` (the rater's
 * `it{N}-per_car_total`, with a legacy line-item-sum fallback), the same resolver staff-fe's
 * breakdown modal uses, then a proportional share of any "gap" between the total premium
 * and the sum of all vehicle-specific premiums is added on top.
 *
 * Background:
 * - Auto policies have vehicle-specific premiums stored in premiumBreakdown with keys like:
 *   - "it1-COLL", "it1-COMP" (iteration 1 = first car's collision and comprehensive)
 *   - "it2-COLL", "it2-COMP" (iteration 2 = second car's coverages)
 * - The total of all iteration-based coverages may not equal the total policy premium due to:
 *   - Policy-level coverages (BI, PD, UM, UIM, Medical Payments)
 *   - Fees, surplus contributions, and other charges
 * - This function calculates: base vehicle premium + (gap / number of cars)
 *
 * @param carIteration - The car's iteration number (1-indexed, e.g., first car = 1)
 * @param premiumBreakdown - The premiumBreakdown object from the policy
 * @param totalPremium - The total policy premium (base premium + surplus contribution + fees)
 * @param numberOfCars - The total number of cars on the policy
 * @returns The premium amount attributed to this specific vehicle
 *
 * @example
 * ```typescript
 * const premiumBreakdown = {
 *   "it1-COLL": 150,
 *   "it1-COMP": 100,
 *   "it2-COLL": 200,
 *   "it2-COMP": 150,
 *   "BI": 300,
 *   "PD": 200
 * };
 * const totalPremium = 1200;
 * const numberOfCars = 2;
 *
 * // First car has COLL + COMP = 250, plus share of gap
 * const car1Premium = calculateVehiclePremiumFromBreakdown(1, premiumBreakdown, totalPremium, numberOfCars);
 * // car1Premium = 250 + (1200 - 600) / 2 = 250 + 300 = 550
 *
 * // Second car has COLL + COMP = 350, plus share of gap
 * const car2Premium = calculateVehiclePremiumFromBreakdown(2, premiumBreakdown, totalPremium, numberOfCars);
 * // car2Premium = 350 + (1200 - 600) / 2 = 350 + 300 = 650
 * ```
 */
export function calculateVehiclePremiumFromBreakdown(
  carIteration: number,
  premiumBreakdown: PremiumBreakdownMap | null | undefined,
  totalPremium: number,
  numberOfCars: number
): number {
  if (numberOfCars <= 0) {
    return 0;
  }

  // If no breakdown data, fall back to simple division
  if (!premiumBreakdown || typeof premiumBreakdown !== 'object') {
    return totalPremium / numberOfCars;
  }

  // Each car's base comes from the resolver shared with staff's breakdown modal
  const vehicleSpecificPremium = resolveCarTotalFromBreakdown(premiumBreakdown, carIteration);

  // Sum all vehicle-specific premiums across all cars
  const allVehiclePremiumsTotal = Array.from({ length: numberOfCars }, (_, i) => i + 1).reduce(
    (total, iteration) => total + resolveCarTotalFromBreakdown(premiumBreakdown, iteration),
    0
  );

  // Calculate the "gap" - the difference between total premium and sum of vehicle-specific premiums
  // This gap includes policy-level coverages (BI, PD, etc.), fees, and other charges
  const gap = totalPremium - allVehiclePremiumsTotal;

  // Distribute the gap evenly across all vehicles
  const gapPerVehicle = gap / numberOfCars;

  // Final vehicle premium = vehicle-specific premium + proportional share of gap
  return vehicleSpecificPremium + gapPerVehicle;
}

/**
 * Calculates the premium amount attributable to a specific vehicle on an auto policy.
 * Simple version that evenly distributes the total premium across all vehicles.
 *
 * Use this when premiumBreakdown data is not available.
 *
 * @param totalPremium - The total policy premium (base premium + surplus contribution + fees)
 * @param numberOfCars - The total number of cars on the policy
 * @returns The premium amount attributed to each vehicle
 *
 * @example
 * ```typescript
 * const premium = 1200;
 * const surplusContribution = 150;
 * const totalFeeCost = 50;
 * const numberOfCars = 2;
 *
 * const totalPremium = premium + surplusContribution + totalFeeCost; // 1400
 * const vehiclePremium = calculateVehiclePremium(totalPremium, numberOfCars); // 700
 * ```
 */
export function calculateVehiclePremium(totalPremium: number, numberOfCars: number): number {
  if (numberOfCars <= 0) {
    return 0;
  }

  return totalPremium / numberOfCars;
}

/**
 * Calculates the "gap" amount per vehicle - the difference between total premium
 * and sum of all vehicle-specific premiums, distributed evenly.
 *
 * This is useful when you already have the vehicle-specific base premium and just
 * need to add the proportional share of policy-level costs (fees, surplus, etc).
 *
 * @param totalPremium - Total policy cost (premium + surplus + fees)
 * @param vehicleSpecificPremiumsTotal - Sum of all vehicle-specific premiums
 * @param numberOfCars - Total number of cars on the policy
 * @returns The gap amount to add to each vehicle's base premium
 *
 * @example
 * ```typescript
 * const totalPremium = 1200;
 * const vehicleSpecificTotal = 600; // Sum of all it1-*, it2-*, etc
 * const numberOfCars = 2;
 *
 * const gapPerVehicle = calculateVehiclePremiumGap(totalPremium, vehicleSpecificTotal, numberOfCars);
 * // gapPerVehicle = (1200 - 600) / 2 = 300
 *
 * // Then add to each vehicle's base premium:
 * // car1Total = car1BasePremium + 300
 * // car2Total = car2BasePremium + 300
 * ```
 */
export function calculateVehiclePremiumGap(
  totalPremium: number,
  vehicleSpecificPremiumsTotal: number,
  numberOfCars: number
): number {
  if (numberOfCars <= 0) {
    return 0;
  }

  const gap = totalPremium - vehicleSpecificPremiumsTotal;
  return gap / numberOfCars;
}

/**
 * Calculates the total policy cost including premium, surplus contribution, and all fees.
 *
 * @param premium - Base policy premium
 * @param surplusContribution - Surplus contribution amount
 * @param fees - Array of fee objects, each containing an 'amount' property
 * @returns The total policy cost
 *
 * @example
 * ```typescript
 * const premium = 1200;
 * const surplusContribution = 150;
 * const fees = [{ amount: 25 }, { amount: 25 }];
 *
 * const total = calculateTotalPolicyCost(premium, surplusContribution, fees); // 1400
 * ```
 */
export function calculateTotalPolicyCost(
  premium: number,
  surplusContribution: number,
  fees: Array<{ amount: number }>
): number {
  const totalFeeCost = fees.reduce((sum, fee) => sum + fee.amount, 0);
  return premium + surplusContribution + totalFeeCost;
}
